import Cookies from 'js-cookie';

import { CometUserRoleType } from '@zg-rentals/rental-manager-env';

import { getCookieAsBoolean } from '../../../src/utils/cookieUtils';

import { ZRM_MULTIPLEX_LISTING_FLOW_ENABLED, ZRM_ONBOARDING_WIZARD_ENABLED } from '../constants/FeatureFlagConstants';

import propertyUtils from './propertyUtils';

import type { AppConfigProps } from '../model/AppConfig';
import type { CurrentPropertyProps } from '../model/CurrentProperty';
import type { RentGuaranteeProps } from '../model/RentGuarantee';
import type { UserProps } from '../model/User';
export type RestrictionsProps = {
  applications?: Record<string, unknown>;
  [key: string]: boolean | undefined | Record<string, unknown>;
};

type CookieEnabledServicesProps = {
  [key: string]: string | boolean;
};

const canAccessApplications = ({ restrictions = {} }: { restrictions?: RestrictionsProps }) => {
  const hasApplicationsProperty = restrictions.hasOwnProperty('applications');
  // isAccessRestricted controls whether the application tab (in the subnav) should be  hidden or displayed
  return !hasApplicationsProperty || !restrictions?.applications?.isAccessRestricted;
};

const canUpdateApplications = ({ restrictions = {} }: { restrictions?: RestrictionsProps }) => {
  const hasApplicationsProperty = restrictions.hasOwnProperty('applications');
  // isUpdateRestricted indicates whether the user is allowed to enable or disable applications on a listing
  return !hasApplicationsProperty || !restrictions?.applications?.isUpdateRestricted;
};

const canAccessLeaseBuilder = ({ restrictions = {} }: { restrictions?: RestrictionsProps }) =>
  !restrictions.isLeaseBuilderRestricted;

const canFeedConnectSwitchSource = ({ user }: { user: { features: { canSwitchListingSource: boolean } } | null }) =>
  user?.features?.canSwitchListingSource;

const canAccessListingClaiming = ({ user }: { user: UserProps | null }) => user?.features?.canAccessFeedClaimAndConvert;

const canAccessModularListing = ({
  currentProperty,
  user,
}: {
  currentProperty: { isMultifamily: boolean; listingTypeCode: number; dateLastActivatedTimestamp: string };
  user: { features: { canAccessModularListing: boolean } };
}) => {
  const { dateLastActivatedTimestamp = '' } = currentProperty;
  const {
    features: { canAccessModularListing },
  } = user;

  const hasListingBeenActivated = dateLastActivatedTimestamp !== '';
  const isSingleFamilyRental = propertyUtils.isSingleFamilyRental({ currentProperty });
  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const response = !hasListingBeenActivated && (isSingleFamilyRental || isRoomForRent) && canAccessModularListing;

  return response;
};

const canAccessAddFeedListingsModal = ({ user }: { user: { features: { canViewClaimFeedListing: boolean } } | null }) =>
  user?.features?.canViewClaimFeedListing;

const canAccessRentalProtectionFromML = ({ rentalProtection }: { rentalProtection: RentGuaranteeProps }) => {
  const { isEligible: isRentGuaranteeEligible } = rentalProtection;
  return isRentGuaranteeEligible;
};

const canAccessRentGuaranteeWithFeatureFlag = ({
  cookieEnabledServices,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
}) => Boolean(cookieEnabledServices.ZRM_RENT_GUARANTEE_ENABLED);

const isTourSchedulingEnabledForProperty = ({
  currentProperty,
  isInReactivationFlow,
}: {
  currentProperty: CurrentPropertyProps;
  isInReactivationFlow: boolean;
}) => {
  const { active, canBeRelisted, isFeed } = currentProperty;
  const canAccessRelisting = canAccessRelistingFlow({ canBeRelisted });
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessDashboardRedesign = ({ currentProperty }: { currentProperty: CurrentPropertyProps }) => {
  return currentProperty?.isMultifamily === false;
};

const canAccessGlobalAnalytics = ({ user }: { user: UserProps }) => user.features.canAccessMainAnalytics;

const canAccessGlobalContacts = ({ user }: { user: UserProps }) => user.features.canAccessMainContacts;

const canAccessBilling = ({ user }: { user: UserProps }) => user.features.canAccessBilling;

const canAccessSettings = ({ user }: { user: UserProps }) => user.features.canAccessMainSecuritySettings;

const canAccessUserManagement = ({ user }: { user: UserProps }) => user.features.canAccessUserManagement;

const canAccessUserManagementApplicationPermission = ({ user }: { user: UserProps }) =>
  user.features.canAccessUserManagementApplicationPermission;

const canAddListing = ({ user }: { user: UserProps }) => user.features.canAddListing;

const canAccessInvoicePayments = ({ user }: { user: UserProps }) => user.features.canAccessInvoicePayments;

const canEditMFFeedContact = ({
  formData,
}: {
  formData?: { restrictions: { isUpdateListingRestricted: boolean }; isFeed: boolean; isContactInfoEditable: boolean };
}) => {
  const { restrictions, isFeed, isContactInfoEditable } = formData || {};

  return isFeed && restrictions?.isUpdateListingRestricted === false && isContactInfoEditable;
};

const canAccessFeatureNotifications = ({
  user,
}: {
  user: { features: { canAccessFeatureLaunchNotifications: boolean } } | null;
}) => user?.features?.canAccessFeatureLaunchNotifications;

const canAccessOnboardingWizard = ({
  user,
  cookieEnabledServices: _cookieEnabledServices,
}: {
  user: { restrictions?: { isAllowed: boolean }; features: { canViewOnboardingFlow: boolean } } | null;
  cookieEnabledServices?: CookieEnabledServicesProps;
}) => {
  return (
    Boolean(
      user?.restrictions?.isAllowed &&
      (getCookieAsBoolean(ZRM_ONBOARDING_WIZARD_ENABLED) || user.features.canViewOnboardingFlow),
    ) || false
  );
};

const canAccessTopOfFunnelPremium = ({
  isFreemiumEnabled,
  isTopOfFunnelEnrollmentEligible,
  isActiveProductTypePremium,
}: {
  isFreemiumEnabled: boolean;
  isTopOfFunnelEnrollmentEligible: boolean;
  isActiveProductTypePremium: boolean;
}) => {
  return isFreemiumEnabled && (isActiveProductTypePremium || isTopOfFunnelEnrollmentEligible);
};

const canAccessMLReactivationFlow = ({ currentProperty }: { currentProperty: Partial<CurrentPropertyProps> }) => {
  return (currentProperty?.dateLastActivatedTimestamp || currentProperty?.isFeed) && !currentProperty?.isMultifamily;
};

const canAccessFastPost = ({ currentProperty }: { currentProperty: CurrentPropertyProps }) => {
  return (
    !currentProperty?.isMultifamily &&
    !currentProperty?.dateLastActivatedTimestamp &&
    !currentProperty?.isFeed &&
    !currentProperty?.restrictions?.applications?.isAccessRestricted
  );
};

const canAccessCloneListing = ({ currentProperty }: { currentProperty: CurrentPropertyProps }) => {
  return currentProperty?.restrictions?.isCloneListingRestricted;
};

const canAccessMaintenanceTracker = ({
  canAccessMaintenanceTracker,
  cookieZGMaintenanceTracker,
}: {
  canAccessMaintenanceTracker: UserProps;
  cookieZGMaintenanceTracker: CookieEnabledServicesProps;
}) => {
  return cookieZGMaintenanceTracker || canAccessMaintenanceTracker;
};

const canAccessRelistingFlow = ({ canBeRelisted }: { canBeRelisted: boolean }) => {
  return canBeRelisted;
};

const canAccessListingPreview = ({ currentProperty }: { currentProperty: CurrentPropertyProps }) => {
  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  return !currentProperty?.active && !isRoomForRent && !currentProperty?.isFeed && !currentProperty?.isMultifamily;
};

const canAccessScreeningCriteria = ({
  isUserEligibleForListingCriteria,
  isListingCriteriaEnabled,
}: {
  isUserEligibleForListingCriteria: boolean;
  isListingCriteriaEnabled: boolean;
}) => {
  return Boolean(isUserEligibleForListingCriteria && isListingCriteriaEnabled);
};

const canAccessMultiBoxVerificationInput = ({
  cookieEnabledServices,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
}) => cookieEnabledServices?.ZRM_MULTI_BOX_VERIFICATION_INPUT_ENABLED;

const canAccessMultiplexGroups = ({
  canAccessMultiplexGroupsResponse,
  cookieEnabledServices,
}: {
  canAccessMultiplexGroupsResponse: string;
  cookieEnabledServices: CookieEnabledServicesProps;
}) => {
  const areRequiredConditionsSatisfied = Boolean(canAccessMultiplexGroupsResponse === 'true');
  const areOverrideConditionsSatisfied = cookieEnabledServices?.ZRM_MULTIPLEX_GROUPS_ENABLED;

  return areRequiredConditionsSatisfied || areOverrideConditionsSatisfied;
};

const canAccessMultiplexListingFlow = ({
  canAccessMultiplexListingFlowResponse,
}: {
  canAccessMultiplexListingFlowResponse: string;
}) => {
  const areRequiredConditionsSatisfied = Boolean(canAccessMultiplexListingFlowResponse === 'true');
  const areOverrideConditionsSatisfied = getCookieAsBoolean(ZRM_MULTIPLEX_LISTING_FLOW_ENABLED);

  return areRequiredConditionsSatisfied || areOverrideConditionsSatisfied;
};

const canAccessZRMMarketingComponents = ({
  cookieEnabledServices,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
}) => cookieEnabledServices?.ZRM_MARKETING_COMPONENTS_ENABLED;

const canAccessSFCostsAndFees = ({ isFeed, isMultifamily }: { isFeed: boolean; isMultifamily: boolean }) =>
  !isFeed && !isMultifamily;

const isNonAdvertiser = ({ user }: { user: { roleTypes: Array<CometUserRoleType> } | null }) =>
  Boolean(user?.roleTypes?.includes(CometUserRoleType.NON_ADVERTISER_CUSTOMER));

const canAccessClaimPath = ({
  cookie,
  hasAnyPropertiesToClaim,
  isScreenMobile,
  isNonAdvertiser,
}: {
  cookie?: string;
  hasAnyPropertiesToClaim: boolean;
  isScreenMobile: boolean;
  isNonAdvertiser: boolean;
}) => !isScreenMobile && (hasAnyPropertiesToClaim || isNonAdvertiser) && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessClaimMorePath = ({
  isScreenMobile,
  hasAnyClaimedListings,
  hasNewPropertiesToClaim,
  cookie,
}: {
  isScreenMobile: boolean;
  hasAnyClaimedListings: boolean;
  hasNewPropertiesToClaim: boolean;
  cookie: string;
}) =>
  !isScreenMobile && hasAnyClaimedListings && hasNewPropertiesToClaim && (!cookie || Cookies.get(cookie) !== 'true');

const shouldShowBrokerFeeAlert = ({ state }: { state: string }) => {
  return state ? state === 'MA' : false;
};

const canAccessListingQuality = ({
  user,
  currentProperty,
}: {
  user: UserProps;
  currentProperty?: CurrentPropertyProps;
}) =>
  Boolean(user.isMultifamilyCustomer || isNonAdvertiser({ user })) &&
  (!currentProperty || Boolean(currentProperty?.isMultifamily));

const canAccessTotalMonthlyPriceWithFee = ({
  cookieEnabledServices,
  isEligible = false,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
  isEligible?: boolean;
}) => Boolean(cookieEnabledServices?.ZRM_TOTAL_MONTHLY_PRICE_ENABLED) || isEligible;

const canAccessSpotlightListing = ({
  appConfig,
  cookieEnabledServices,
}: {
  appConfig: AppConfigProps;
  cookieEnabledServices: CookieEnabledServicesProps;
}) =>
  Boolean(
    appConfig?.config?.features?.canAccessSpotlightListing || cookieEnabledServices?.ZRM_SPOTLIGHT_LISTING_ENABLED,
  );

const canAccessSpotlightListingsFirstUseModal = ({
  isScreenMobile,
  isSpotlightListingsEligible,
  appConfig: _appConfig,
  cookieEnabledServices: _cookieEnabledServices,
  cookie,
}: {
  isScreenMobile: boolean;
  isSpotlightListingsEligible: boolean;
  appConfig?: AppConfigProps;
  cookieEnabledServices?: CookieEnabledServicesProps;
  cookie?: string;
}) => Boolean(!isScreenMobile && isSpotlightListingsEligible && (!cookie || Cookies.get(cookie) !== 'true'));

export default {
  canAccessAddFeedListingsModal,
  canAccessApplications,
  canAccessBilling,
  canAccessClaimPath,
  canAccessClaimMorePath,
  canAccessCloneListing,
  canAccessDashboardRedesign,
  canAccessFastPost,
  canAccessFeatureNotifications,
  canAccessGlobalAnalytics,
  canAccessGlobalContacts,
  canAccessInvoicePayments,
  canAccessLeaseBuilder,
  canAccessListingClaiming,
  canAccessListingPreview,
  canAccessMLReactivationFlow,
  canAccessMaintenanceTracker,
  canAccessModularListing,
  canAccessMultiBoxVerificationInput,
  canAccessMultiplexGroups,
  canAccessMultiplexListingFlow,
  canAccessOnboardingWizard,
  canAccessRelistingFlow,
  canAccessRentGuaranteeWithFeatureFlag,
  canAccessRentalProtectionFromML,
  canAccessScreeningCriteria,
  canAccessSettings,
  canAccessTopOfFunnelPremium,
  canAccessUserManagement,
  canAccessUserManagementApplicationPermission,
  canAccessZRMMarketingComponents,
  canAddListing,
  canEditMFFeedContact,
  canFeedConnectSwitchSource,
  canUpdateApplications,
  canAccessSFCostsAndFees,
  isTourSchedulingEnabledForProperty,
  canAccessListingQuality,
  isNonAdvertiser,
  canAccessTotalMonthlyPriceWithFee,
  shouldShowBrokerFeeAlert,
  canAccessSpotlightListing,
  canAccessSpotlightListingsFirstUseModal,
};
